import { useState, useMemo } from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { L } from './ui/LavDropdown'
import { BkIc, bkSearchSx, bkGhostSx } from './bookings/bkBits'
import FutureAppointments from './FutureAppointments'
import PendingAppointments from './PendingAppointments'

/**
 * BookingManager — merchant Bookings workspace.
 *
 * Desktop follows the Claude Design "Bookings · desktop" spec: a page header
 * with the section title, pill sub-tabs (with a live count on Requests) and
 * header actions (search + an Open-calendar shortcut on Upcoming), over the
 * per-tab lists. Below the `md` breakpoint it switches to "Mobile · bookings":
 * a stacked title row, full-width segmented tabs and a surface search field.
 *
 * Requests renders PendingAppointments, and Upcoming/Past render
 * FutureAppointments in its two modes. Search filters the active tab's rows.
 * The design's Waitlist tab is intentionally not implemented.
 */

const BK_TABS = [
	{ id: 'requests', label: 'Requests' },
	{ id: 'upcoming', label: 'Upcoming' },
	{ id: 'past', label: 'History' },
]

const SEARCH_PH = {
	requests: 'Search requests',
	upcoming: 'Search upcoming',
	past: 'Search history',
}

function BookingManager({ merchant, onAppointmentClick, pendingAppointments, onOpenCalendar, onMessageClient }) {
	const [activeTab, setActiveTab] = useState('requests')
	const [query, setQuery] = useState('')
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	// Requests badge counts distinct booking requests (a multi-pet request is one
	// group), matching how PendingAppointments groups them for display.
	const requestCount = useMemo(() => {
		const ids = new Set()
		;(pendingAppointments || []).forEach((a) => ids.add(a.group_id || a.id))
		return ids.size
	}, [pendingAppointments])

	const tabCount = { requests: requestCount }

	// ── tab buttons (pill on desktop, full-width segmented on mobile) ──
	const renderTab = (t) => {
		const on = activeTab === t.id
		const n = tabCount[t.id]
		return (
			<Box
				component="button"
				type="button"
				role="tab"
				aria-selected={on}
				key={t.id}
				onClick={() => setActiveTab(t.id)}
				sx={{
					display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
					gap: isMobile ? '5px' : '7px', height: isMobile ? 36 : 34,
					px: isMobile ? '4px' : '14px', border: 'none', borderRadius: '9px',
					flex: isMobile ? 1 : 'none', minWidth: 0,
					bgcolor: on ? '#FFFFFF' : 'transparent', color: on ? L.text : L.muted,
					fontSize: isMobile ? '12.5px' : '13px', fontWeight: 700, fontFamily: 'inherit',
					cursor: 'pointer', whiteSpace: 'nowrap',
					boxShadow: on ? '0 1px 3px rgba(46,42,64,0.10)' : 'none',
					'&:hover': { color: L.text },
				}}
			>
				<span>{t.label}</span>
				{n > 0 && (
					<Box component="span" sx={{
						display: 'inline-flex', alignItems: 'center',
						height: isMobile ? 17 : 20, px: isMobile ? '5px' : '7px',
						borderRadius: '99px', fontSize: isMobile ? '10.5px' : '11px', fontWeight: 750,
						bgcolor: on ? L.primarySoft : '#E7E2F3',
						color: on ? L.primaryDeep : '#6E6886',
					}}>
						{n}
					</Box>
				)}
			</Box>
		)
	}

	const searchBox = (mobile) => (
		<Box component="label" sx={mobile
			? {
				display: 'flex', alignItems: 'center', gap: '8px', mt: '12px', height: 40,
				px: '12px', borderRadius: '11px', bgcolor: L.surface, color: L.muted,
				'& input': {
					border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit',
					fontSize: '15px', color: L.text, width: '100%', p: 0,
					'&::placeholder': { color: L.muted, opacity: 1 },
				},
			}
			: { ...bkSearchSx, width: 230 }}
		>
			<BkIc name="search" size={mobile ? 16 : 15} />
			<Box
				component="input"
				type="text"
				placeholder={SEARCH_PH[activeTab]}
				value={query}
				onChange={(e) => setQuery(e.target.value)}
			/>
		</Box>
	)

	const content = (
		<>
			{activeTab === 'requests' && (
				<PendingAppointments
					merchant={merchant}
					onAppointmentClick={onAppointmentClick}
					onMessageClient={onMessageClient}
					appointments={pendingAppointments}
					query={query}
				/>
			)}

			{activeTab === 'upcoming' && (
				<FutureAppointments
					merchant={merchant}
					onAppointmentClick={onAppointmentClick}
					onMessageClient={onMessageClient}
					query={query}
				/>
			)}

			{activeTab === 'past' && (
				<FutureAppointments
					merchant={merchant}
					onAppointmentClick={onAppointmentClick}
					onMessageClient={onMessageClient}
					mode="past"
					query={query}
				/>
			)}
		</>
	)

	return (
		<Box sx={{
			height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
			bgcolor: isMobile ? L.bg : '#FBFAFE', borderRadius: isMobile ? 0 : '12px',
			overflow: 'hidden', fontFamily: L.font, color: L.text,
		}}>
			<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
				{isMobile ? (
					<>
						{/* ── mobile head: title row + segmented tabs + search (sticky) ── */}
						<Box sx={{
							position: 'sticky', top: 0, zIndex: 5, bgcolor: L.bg,
							borderBottom: `1px solid ${L.border}`, p: '14px 16px 12px',
						}}>
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
								<Box component="h1" sx={{ fontSize: '30px', fontWeight: 800, m: 0, letterSpacing: '-0.02em' }}>
									Bookings
								</Box>
								{activeTab === 'upcoming' && onOpenCalendar && (
									<Box component="button" type="button" onClick={onOpenCalendar} sx={{ ...bkGhostSx(false), flex: 'none' }}>
										<BkIc name="calendar" size={14} />
										<span>Calendar</span>
									</Box>
								)}
							</Box>
							{/* .bkm-tabs — full-width segmented */}
							<Box role="tablist" sx={{
								display: 'flex', gap: '3px', p: '4px', mt: '12px',
								bgcolor: L.surface, borderRadius: '12px',
							}}>
								{BK_TABS.map(renderTab)}
							</Box>
							{searchBox(true)}
						</Box>

						{/* .bkm-list */}
						<Box sx={{ p: '12px 16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
							{content}
						</Box>
					</>
				) : (
					<Box sx={{ p: '22px 26px 30px' }}>
						{/* ── desktop head: title + pill sub-tabs + header actions ── */}
						<Box sx={{
							display: 'flex', alignItems: 'center', justifyContent: 'space-between',
							gap: '16px', mb: '18px', flexWrap: 'wrap',
						}}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', minWidth: 0 }}>
								<Box component="h1" sx={{ fontSize: '23px', fontWeight: 800, m: 0, letterSpacing: '-0.02em' }}>
									Bookings
								</Box>
								<Box role="tablist" sx={{
									display: 'inline-flex', gap: '3px', p: '4px', bgcolor: L.surface,
									border: `1px solid ${L.border}`, borderRadius: '12px', maxWidth: '100%',
									overflowX: 'auto', scrollbarWidth: 'none',
									'&::-webkit-scrollbar': { display: 'none' },
								}}>
									{BK_TABS.map(renderTab)}
								</Box>
							</Box>

							{/* .bk-headactions — search (all tabs) */}
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
								{searchBox(false)}
							</Box>
						</Box>

						{content}
					</Box>
				)}
			</Box>
		</Box>
	)
}

export default BookingManager
